// TODO: (vliu) This is used in architect only and can be removed after we update architect
// to use same list style as recordings page

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { Clear, Search } from '@mui/icons-material';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from 'react';
import styled, { css, ThemeContext } from 'styled-components/macro';

import { DropdownListContainer } from './DropdownList';
import { SVGIconButton } from './SVGIconButton';

const DropdownInputContainer = styled.div<{
  minwidth: string | undefined;
  isReadOnly: boolean;
}>`
  ${(props) => (props.minwidth ? `min-width: ${props.minwidth};` : '')}
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;

  background-color: ${(props) => props.theme.bgColor};
  border-radius: 4px;
  border: 1px solid
    ${(props) =>
      props.isReadOnly
        ? props.theme.inputReadOnlyColor
        : props.theme.inputDefaultColor};

  /* Allow for the dropdown list to be positioned relative to this form
  container*/
  position: relative;

  ${(props) =>
    props.isReadOnly
      ? ''
      : css`
          &:focus-within {
            /* Change outline of container and color of icon when the text
             input is focused */
            border: 1px solid ${props.theme.inputFocusedColor};
            ${SVGIconButton} {
              color: ${props.theme.inputFocusedColor};
            }

            /* If a dropdownlist a child, always reveal it when focused */
            ${DropdownListContainer} {
              display: flex;
            }
          }
        `}
`;

const StyledTextInput = styled.input`
  /* Reset input styling */
  color: ${(props) => props.theme.textDefaultColor};

  /* Allow the input to span the size of its container */
  min-width: 0;
  flex-grow: 1;

  /* Make placeholder a bit darker */
  &::placeholder {
    color: var(--color-gravity-gray);
    opacity: 80%;
  }

  /* Since we add the input to a container, remove the focus outline */
  &:focus {
    outline: none;
  }
`;

const DropdownInput: React.FC<{
  placeholder: string;
  minWidth?: string;
  userInput: string;
  onInputChange: (newInput: string) => void;
  setInputHandle?: (input: HTMLInputElement) => void;
  onKeyboardDown?: () => void;
  onKeyboardUp?: () => void;
  onKeyboardEnter?: () => void;
  onFocusChange?: (isFocused: boolean) => void;
  disabled?: boolean;
}> = ({
  placeholder,
  minWidth,
  userInput,
  onInputChange,
  setInputHandle,
  onKeyboardDown,
  onKeyboardUp,
  onKeyboardEnter,
  onFocusChange,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Give input handles on other components so other parts of the page
  // can set focus to this input
  useEffect(() => {
    if (setInputHandle && inputRef.current) {
      setInputHandle(inputRef.current);
    }
  }, [setInputHandle]);

  const inputKeyHandler = useCallback(
    (ev: SyntheticEvent<HTMLInputElement, KeyboardEvent>) => {
      switch (ev.nativeEvent.key) {
        case 'ArrowDown':
          onKeyboardDown?.();
          break;
        case 'ArrowUp':
          onKeyboardUp?.();
          break;
        case 'Enter':
          onKeyboardEnter?.();
          break;
        case 'Escape':
          const el = ev.currentTarget as HTMLInputElement;
          el.blur();
          break;
        default:
      }

      // When the user types something in the input, stop propagation to
      // the visualization key bindings since here the user is only
      // typing and not interacting with the point cloud
      ev.stopPropagation();
      ev.nativeEvent.stopImmediatePropagation();
    },
    [onKeyboardUp, onKeyboardDown, onKeyboardEnter]
  );

  return (
    <DropdownInputContainer minwidth={minWidth} isReadOnly={false}>
      <StyledTextInput
        type="text"
        value={userInput}
        ref={inputRef}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDownCapture={inputKeyHandler}
        onFocus={() => {
          onFocusChange?.(true);
        }}
        onBlur={() => {
          onFocusChange?.(false);
        }}
        disabled={disabled}
      />
      {/* Focus on the input when the Search icon is pressed */}
      <SVGIconButton
        onClick={() => {
          if (inputRef.current && !disabled) {
            inputRef.current.focus();
          }
        }}
        size="24px"
        disabled={disabled}
      >
        <Search />
      </SVGIconButton>
    </DropdownInputContainer>
  );
};

const DropdownInputReadOnly: React.FC<{
  input: string;
  onReset: () => void;
  minWidth?: string;
}> = ({ input, minWidth, onReset }) => {
  const themeContext = useContext(ThemeContext);

  return (
    <DropdownInputContainer minwidth={minWidth} isReadOnly>
      <div>{input}</div>
      <SVGIconButton
        onClick={() => {
          onReset();
        }}
        size="24px"
        color={themeContext.inputReadOnlyColor}
      >
        <Clear />
      </SVGIconButton>
    </DropdownInputContainer>
  );
};

export { DropdownInput, DropdownInputReadOnly };
